import React, { useState, useRef } from 'react';
import classNames from 'classnames';

import BaseInput from '../Input/BaseInput';
import OptionList, { OptionListOption } from '../OptionList/OptionList';
import { BaseInputPropsWithoutChildren } from '../Input/BaseInput';

import isIE11 from '../../shared/utils/isIE11/isIE11';
import { CreateProps } from '../../types/utils/CreateProps';

import styles from './Combobox.module.scss';

type ComboboxProps = CreateProps<
  {
    /** Label shown on the Combobox */
    label?: string;
    /** List of options */
    options: OptionListOption[];
    /** Text to be shown in the Combobox */
    value?: string;
    /** onChange is called when text inside input is changed, will return the native event and value (event, value) */
    onChange: (
      event:
        | React.SyntheticEvent<HTMLDivElement>
        | JSX.IntrinsicElements['input']['onChange'],
      value: string
    ) => void;
    /** onOptionSelect is called when user makes selection from the list. It will return native event and option object (event, option)*/
    onOptionSelect?: (
      event:
        | React.SyntheticEvent<HTMLDivElement>
        | React.ChangeEvent<HTMLInputElement>
        | undefined,
      option: OptionListOption
    ) => void;
    /**  onOptionClear is called an option becomes unselected. In case there is a selected option then any text change will cause this to be called once.  Will return native event (option, event)*/
    onOptionClear?: (event: React.SyntheticEvent<HTMLElement>) => void;

    /** Error text is shown if Combobox is not open and will disappear when user opens the Combobox  */
    errorText?: string;
    /** Additional description for input, if label is not enough  */
    helperText?: string;
    /** Placeholder is visible when the Combobox is empty */
    placeholder?: string;
    /** Sets the Combobox to disabled state. Dropdown is not clickable nor focusable */
    disabled?: boolean;
    /** Adds the required mark on the Combobox label */
    required?: boolean;
    /** Custom id */
    id?: string;
    /** Custom name */
    name?: string;
    /** Custom className */
    className?: string;
  },
  BaseInputPropsWithoutChildren,
  | 'readonly'
  | 'iconName'
  | 'iconColor'
  | 'tabIndex'
  | 'onClick'
  | 'ref'
  | 'borderRadius'
  | 'onChange'
  | 'onBlur'
  | 'onFocus'
  | 'confirmed'
  | 'wrapperTabIndex'
>;

const Combobox: React.FunctionComponent<ComboboxProps> = ({
  options,
  onChange,
  value,
  errorText,
  disabled,
  className,
  onOptionSelect,
  onOptionClear,
  ...props
}) => {
  const preselectedOptionId = options.find((option) => option.label === value)
    ?.value;

  const [open, setOpen] = useState(false);
  const [focusedOptionId, setFocusedOptionId] = useState(preselectedOptionId);
  const [selectedOptionId, setSelectedOptionId] = useState(preselectedOptionId);

  const inputElement = useRef<HTMLInputElement>(null);
  const dropdownElement = useRef<HTMLDivElement>(null);

  const focusOnFirstFocusableOption = () => {
    const firstFocusableOption = options.filter(
      (option) => !option.disabled
    )[0];
    /* istanbul ignore else */
    if (firstFocusableOption) {
      setFocusedOptionId(firstFocusableOption.value);
    }
  };

  const getSelectedOption = () => {
    return options.find((option) => option.value === selectedOptionId);
  };

  const findOptionByLabel = (label: string) => {
    return options.find((option) => option.label === label);
  };

  const selectOption = (
    event:
      | React.SyntheticEvent<HTMLDivElement>
      | React.ChangeEvent<HTMLInputElement>
      | undefined,

    option: OptionListOption
  ) => {
    setSelectedOptionId(option.value);
    setFocusedOptionId(option.value);
    onOptionSelect && onOptionSelect(event, option);
  };

  if (open && preselectedOptionId && !selectedOptionId && !focusedOptionId) {
    const optionToBeSelected = options.find((option) => option.label === value);
    optionToBeSelected && selectOption(undefined, optionToBeSelected);
    setOpen(false);
  }

  const isOpenWithSelection = () =>
    open && typeof selectedOptionId !== 'undefined';

  const openOptionsAndPreselectOption = (
    event:
      | React.KeyboardEvent<HTMLDivElement>
      | React.MouseEvent<HTMLDivElement, MouseEvent>
  ) => {
    const selectedOption = getSelectedOption();
    if (selectedOption) {
      updateInputText(event, '');
      setFocusedOptionId(selectedOption.value);
    }
    setOpen(true);
  };

  const handleComponentMouseDown = (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>
  ) => {
    event.preventDefault();
    event.stopPropagation();
    if (open) {
      const selectedOption = getSelectedOption();
      setOpen(false);
      if (selectedOption) {
        updateInputText(event, selectedOption.label);
      }
    } else if (!value) {
      openOptionsAndPreselectOption(event);
    } else {
      setOpen(true);
    }
    inputElement.current?.focus();
  };

  const handleOptionListBlur = (
    event:
      | React.FocusEvent<HTMLDivElement>
      | React.KeyboardEvent<HTMLDivElement>
  ) => {
    const isFocusEvent = (
      e: typeof event
    ): e is React.FocusEvent<HTMLDivElement> => 'relatedTarget' in e;

    /* This logic is related to the keyboard usability (closing on ESC) which is not supported in IE. That's why we leave IE out  */
    event.preventDefault();
    event.stopPropagation();

    /* istanbul ignore next */
    if (!isIE11) {
      if (
        !isFocusEvent(event) ||
        (!dropdownElement.current?.contains(
          event.relatedTarget as HTMLElement
        ) &&
          open)
      ) {
        const selectedOption = getSelectedOption();
        if (selectedOption) {
          updateInputText(event, selectedOption.label);
        }
        setOpen(false);
        inputElement.current?.focus();
      }
    }
  };

  const handleOptionSelect = (
    event: React.SyntheticEvent<HTMLDivElement>,
    option: OptionListOption
  ) => {
    inputElement.current?.focus();
    setOpen(false);
    updateInputText(event, option.label);
    selectOption(event, option);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape' && open) {
      setOpen(false);
    }

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      focusOnFirstFocusableOption();
      event.preventDefault();
      openOptionsAndPreselectOption(event);
    }
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputText = event.target?.value;
    updateInputText(event, inputText);
    if (selectedOptionId && onOptionClear) {
      onOptionClear(event);
    }
    setFocusedOptionId(undefined);
    setSelectedOptionId(undefined);
    const optionWithMatchingLabel = findOptionByLabel(inputText);
    /* istanbul ignore else */
    if (optionWithMatchingLabel) {
      selectOption(event, optionWithMatchingLabel);
      setOpen(false);
    } else if (!open) {
      setOpen(true);
    }
  };

  /* istanbul ignore next */
  const handleOptionMouseDown = (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>
  ) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleInputBlur = (
    event:
      | React.SyntheticEvent<HTMLDivElement>
      | JSX.IntrinsicElements['input']['onChange']
  ) => {
    /* istanbul ignore else */
    if (open && focusedOptionId === undefined) {
      setOpen(false);
      updateInputText(event, value || '');
    }
  };

  const updateInputText = (
    event:
      | React.SyntheticEvent<HTMLDivElement>
      | JSX.IntrinsicElements['input']['onChange'],
    newValue: string
  ) => {
    /* istanbul ignore else */
    if (newValue !== value) {
      onChange(event, newValue);
    }
  };

  return (
    <div
      className={classNames([
        styles.combobox,
        {
          [styles.combobox_open]: open,
        },
        className,
      ])}
      ref={dropdownElement}
      onKeyDown={handleKeyDown}
      onMouseDown={disabled ? undefined : handleComponentMouseDown}
    >
      <BaseInput
        {...props}
        disabled={disabled}
        wrapperTabIndex={-1}
        confirmed={false}
        iconName={open ? 'chevron-up' : 'chevron-down'}
        iconColor={open ? 'purple500' : 'black'}
        value={isOpenWithSelection() ? getSelectedOption()?.label : value}
        readonly={disabled}
        onChange={disabled ? undefined : handleInputChange}
        onBlur={handleInputBlur}
        ref={inputElement}
        errorText={errorText && errorText === ' ' ? ' ' : errorText}
        borderRadius={{ size: 'sm' }}
      />
      {open && (
        <OptionList
          options={options}
          onBlur={handleOptionListBlur}
          optionOnClick={handleOptionSelect}
          focusedOptionId={focusedOptionId}
          onMouseDown={handleOptionMouseDown}
        />
      )}
    </div>
  );
};

export default Combobox;
